import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { RootState } from '../../../reducers';
import * as actions from '../../../actions/generalActions';
import * as adminActions from '../../../actions/admin';
import * as productActions from '../../../actions/production';
import MikaPageHeader from '../../../components/MikaPageHeader/MikaPageHeader';
import ModalComponent from '../../../components/Modal/ModalComponent';
import DeleteModalComponent from '../../../components/Modal/DeleteModal';
import { Input, EInputTextType } from '../../../components/Input/Input';
import { Button, EButtonType } from '../../../components/Button';
import { t } from '../../../i18n';
import { SelectOption } from '../../../types/selectOption.type';
import { ArticleListItem } from '../../../types/articleListItem.type';
import ArticleTabs, { ArticleMode } from './components/ArticleTabs';
import ArticleFilters from './components/ArticleFilters';
import ArticleRow from './components/ArticleRow';
import ArticleEmptyState from './components/ArticleEmptyState';
import PaginationComponent from '../../../components/Pagination/PaginationComponent';
import '../../../components/Pagination/Pagination.scss';
import './articles.scss';

interface Props {
    history: any;
    actions: any;
    adminActions: any;
    productActions: any;
    articles: ArticleListItem[];
    departments: SelectOption[];
    admin: any;
    loading: boolean;
    companyProfile?: { articlesImportEnabled?: boolean } | null;
}

const ArticlesPage = (props: Props) => {
    const intl = useIntl();
    const [mode, setMode] = useState<ArticleMode>('all');
    const [search, setSearch] = useState('');
    const [departmentId, setDepartmentId] = useState<number | null>(null);
    const [supplierId, setSupplierId] = useState<number | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<ArticleListItem | null>(null);
    const [warehouseTarget, setWarehouseTarget] = useState<ArticleListItem | null>(null);
    const [warehouseQty, setWarehouseQty] = useState<number | ''>('');
    const [page, setPage] = useState(1);
    // 0 is the "view all" sentinel that the shared PaginationComponent expects.
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);

    // Per-company gated Excel import (companyProfile.articlesImportEnabled).
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const importInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        props.actions.fetchArticles();
        props.actions.getAllDepartments();
        props.adminActions.getSuppliers();
    }, []);

    const supplierOptions: SelectOption[] = useMemo(() => {
        const list = props.admin?.suppliers || [];
        return list.map((s: any) => ({ value: s.id, label: s.supplier }));
    }, [props.admin?.suppliers]);

    const allArticles = props.articles || [];

    const counts = useMemo(() => {
        let production = 0;
        let purchased = 0;
        for (const a of allArticles) {
            if (a.forProduction) production++;
            else purchased++;
        }
        return { all: allArticles.length, production, purchased };
    }, [allArticles]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return allArticles.filter((a) => {
            if (mode === 'production' && !a.forProduction) return false;
            if (mode === 'purchased' && a.forProduction) return false;
            if (departmentId != null && a.department?.value !== departmentId) return false;
            if (supplierId != null && !(a.suppliers || []).some((s) => s.value === supplierId)) return false;
            if (term) {
                const haystack = `${a.label || ''} ${a.productCode || ''}`.toLowerCase();
                if (!haystack.includes(term)) return false;
            }
            return true;
        });
    }, [allArticles, mode, search, departmentId, supplierId]);

    // Reset to page 1 whenever the filtered result set changes (filter / search /
    // tab / rows-per-page). Without this, deep filters could leave the user on a
    // page that no longer exists.
    useEffect(() => {
        setPage(1);
    }, [mode, search, departmentId, supplierId, rowsPerPage]);

    const showingAll = rowsPerPage === 0;
    const effectiveRpp = showingAll ? Math.max(filtered.length, 1) : rowsPerPage;
    const totalPages = Math.max(1, Math.ceil(filtered.length / effectiveRpp));
    const safePage = Math.min(page, totalPages);
    const pageStartIdx = showingAll ? 0 : (safePage - 1) * effectiveRpp;
    const pageEndIdx = showingAll ? filtered.length : Math.min(pageStartIdx + effectiveRpp, filtered.length);
    const pagedArticles = useMemo(
        () => filtered.slice(pageStartIdx, pageEndIdx),
        [filtered, pageStartIdx, pageEndIdx],
    );

    const pagination = {
        currentPage: safePage,
        lastPage: totalPages,
        perPage: rowsPerPage,
        total: filtered.length,
    };

    const isLoading = props.loading && allArticles.length === 0;
    const isFirstRun = !isLoading && allArticles.length === 0;
    const isNoResults = !isLoading && allArticles.length > 0 && filtered.length === 0;

    const clearFilters = () => {
        setSearch('');
        setMode('all');
        setDepartmentId(null);
        setSupplierId(null);
    };

    const handleModeChange = (next: ArticleMode) => {
        setMode(next);
        if (next === 'production') setSupplierId(null);
        if (next === 'purchased') setDepartmentId(null);
    };

    const goCreate = () => props.history.push('/admin/articles/new');
    const goEdit = (a: ArticleListItem) => props.history.push(`/admin/articles/${a.value}`);
    const stubToast = (key: string) => toast.info(t(key));

    const confirmDelete = () => {
        if (!deleteTarget) return;
        // deleteArticle action already toasts success / "in production" error — don't duplicate.
        props.adminActions.deleteArticle(deleteTarget.value).then(() => {
            setDeleteTarget(null);
            props.actions.fetchArticles();
        });
    };

    const submitWarehouse = () => {
        if (!warehouseTarget || warehouseQty === '' || Number(warehouseQty) <= 0) {
            toast.error(t('admin.articles.warehouse.fillQuantity'));
            return;
        }
        props.productActions
            .addArticlesToWarehouse({ articleId: warehouseTarget.value, quantity: Number(warehouseQty) })
            .then(() => {
                toast.success(t('admin.articles.warehouse.toastSuccess'));
                setWarehouseTarget(null);
                setWarehouseQty('');
            });
    };

    const closeImport = () => {
        setIsImportOpen(false);
        setImportFile(null);
    };

    const submitImport = () => {
        if (!importFile) {
            toast.error(intl.formatMessage({ id: 'admin.articles.import.fileRequired', defaultMessage: 'Ju lutemi zgjidhni një skedar .xlsx.' }));
            return;
        }
        const fd = new FormData();
        fd.append('articles', importFile);
        setImporting(true);
        props.adminActions
            .importArticles(fd)
            .then((result: any) => {
                if (result?.success) {
                    const s = result.summary || {};
                    toast.success(intl.formatMessage(
                        { id: 'admin.articles.import.success', defaultMessage: 'U importuan: {created} të krijuar, {updated} të përditësuar, {stages} faza të bashkëngjitura.' },
                        { created: s.created ?? 0, updated: s.updated ?? 0, stages: s.production_stages_attached ?? 0 },
                    ));
                    closeImport();
                    props.actions.fetchArticles();
                }
                // result == null → importArticles already surfaced the error toast.
            })
            .finally(() => setImporting(false));
    };

    return (
        <div className="art-page">
            <MikaPageHeader
                title={<FormattedMessage id="admin.articles.pageTitle" defaultMessage="Artikujt" />}
                subtitle={intl.formatMessage({
                    id: 'admin.articles.subtitle',
                    defaultMessage: 'Menaxho artikujt për prodhim dhe ata të blerë nga furnitorët',
                })}
                actions={
                    <div className="art-header-actions">
                        {props.companyProfile?.articlesImportEnabled && (
                            <button type="button" className="art-btn art-btn--ghost" onClick={() => setIsImportOpen(true)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                                </svg>
                                <FormattedMessage id="admin.articles.import.button" defaultMessage="Importo" />
                            </button>
                        )}
                        <button type="button" className="art-btn art-btn--primary" onClick={goCreate}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M12 5v14" />
                            </svg>
                            <FormattedMessage id="admin.articles.btnCreate" defaultMessage="Krijo artikullin" />
                        </button>
                    </div>
                }
            />

            <ArticleTabs
                mode={mode}
                onChange={handleModeChange}
                allCount={counts.all}
                productionCount={counts.production}
                purchasedCount={counts.purchased}
            />

            <ArticleFilters
                mode={mode}
                search={search}
                onSearchChange={setSearch}
                departmentOptions={props.departments || []}
                selectedDepartmentId={departmentId}
                onDepartmentChange={setDepartmentId}
                supplierOptions={supplierOptions}
                selectedSupplierId={supplierId}
                onSupplierChange={setSupplierId}
            />

            {isLoading ? (
                <div className="art-list-placeholder" aria-busy="true">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="art-skeleton-row">
                            <div className="art-skeleton-thumb" />
                            <div className="art-skeleton-lines">
                                <div className="art-skeleton-line art-skeleton-line--name" />
                                <div className="art-skeleton-line art-skeleton-line--meta" />
                            </div>
                            <div className="art-skeleton-pill" />
                            <div className="art-skeleton-line art-skeleton-line--value" />
                            <div className="art-skeleton-line art-skeleton-line--value" />
                            <div className="art-skeleton-line art-skeleton-line--price" />
                            <div className="art-skeleton-dot" />
                        </div>
                    ))}
                </div>
            ) : isFirstRun ? (
                <ArticleEmptyState variant="first-run" onCreate={goCreate} />
            ) : isNoResults ? (
                <ArticleEmptyState variant="no-results" onClearFilters={clearFilters} />
            ) : (
                <>
                    <div className="art-list">
                        {pagedArticles.map((a) => (
                            <ArticleRow
                                key={a.value}
                                article={a}
                                onOpen={goEdit}
                                onEdit={goEdit}
                                onDuplicate={() => stubToast('admin.articles.action.duplicate.stub')}
                                onViewUsage={() => stubToast('admin.articles.action.viewUsage.stub')}
                                onAddToWarehouse={(art) => { setWarehouseTarget(art); setWarehouseQty(''); }}
                                onDelete={(art) => setDeleteTarget(art)}
                            />
                        ))}
                    </div>

                    <PaginationComponent
                        currentPage={safePage}
                        perPage={rowsPerPage}
                        pagination={pagination}
                        onPageChange={setPage}
                        onPerPageChange={setRowsPerPage}
                        label={intl.formatMessage({ id: 'admin.articles.pagination.recordsLabel', defaultMessage: 'artikuj' })}
                    />
                </>
            )}

            <ModalComponent isModalHeader isOpen={!!deleteTarget} close={() => setDeleteTarget(null)}>
                <DeleteModalComponent
                    history={props.history}
                    close={() => setDeleteTarget(null)}
                    deleteFunction={confirmDelete}
                />
            </ModalComponent>

            <ModalComponent isModalHeader isOpen={!!warehouseTarget} close={() => setWarehouseTarget(null)}>
                <div className="w-100 px-3">
                    <div className="warehouseModal-header">
                        <p>
                            <FormattedMessage id="admin.articles.warehouse.title" defaultMessage="Shto produkt në depo" />
                        </p>
                    </div>
                    {warehouseTarget && (
                        <p className="mb-3">
                            <FormattedMessage
                                id="admin.articles.warehouse.targetLabel"
                                defaultMessage="Artikulli: {name}"
                                values={{ name: <strong>{warehouseTarget.label}</strong> }}
                            />
                        </p>
                    )}
                    <Input
                        name="warehouseQuantity"
                        value={warehouseQty}
                        defaultValue=""
                        onChange={(_n: string, value: any) => setWarehouseQty(value === '' ? '' : Number(value))}
                        type="number"
                        inputWrapperClass="w-100"
                        inputTextType={EInputTextType.SimpleInput}
                    >
                        <FormattedMessage id="admin.articles.warehouse.amount" defaultMessage="Sasia e produkteve për depo" />
                    </Input>
                    <div className="d-flex justify-content-end align-items-center mt-4">
                        <Button
                            className="w-50"
                            buttonType={EButtonType.GreyButton}
                            onClick={submitWarehouse}
                        >
                            <FormattedMessage id="admin.articles.btnAddToWarehouse" defaultMessage="Shto produkt në depo" />
                        </Button>
                    </div>
                </div>
            </ModalComponent>

            <ModalComponent isModalHeader isOpen={isImportOpen} close={closeImport}>
                <div className="w-100 px-3 art-import-modal">
                    <div className="warehouseModal-header">
                        <p>
                            <FormattedMessage id="admin.articles.import.modalTitle" defaultMessage="Importo artikujt" />
                        </p>
                    </div>
                    <p className="mb-3 art-import-help">
                        <FormattedMessage id="admin.articles.import.help" defaultMessage="Ngarko një skedar Excel (.xlsx) me artikujt." />
                    </p>
                    <input
                        ref={importInputRef}
                        type="file"
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        onChange={(e) => setImportFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                    />
                    <div className="art-import-filebar">
                        <button type="button" className="art-btn art-btn--ghost" onClick={() => importInputRef.current?.click()} disabled={importing}>
                            <FormattedMessage id="admin.articles.import.chooseFile" defaultMessage="Zgjidh skedarin" />
                        </button>
                        <span className="art-import-filename">{importFile?.name}</span>
                    </div>
                    <div className="art-import-actions">
                        <button type="button" className="art-btn art-btn--ghost" onClick={closeImport} disabled={importing}>
                            <FormattedMessage id="admin.articles.import.cancel" defaultMessage="Anulo" />
                        </button>
                        <button type="button" className="art-btn art-btn--primary" onClick={submitImport} disabled={importing}>
                            {importing
                                ? <FormattedMessage id="admin.articles.import.importing" defaultMessage="Duke importuar…" />
                                : <FormattedMessage id="admin.articles.import.button" defaultMessage="Importo" />}
                        </button>
                    </div>
                </div>
            </ModalComponent>
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        admin: state.admin,
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
        productActions: bindActionCreators(productActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ArticlesPage as any));
